"use client";

import { toPlainDateString } from "@sa-nu/shared-utils/date";
import { useEffect, useRef } from "react";

import { SiteAboutFacilities } from "../../../(default)/areas/[areaSlug]/sites/[siteSlug]/_components/site-about-facilities";
import { SiteAboutOverview } from "../../../(default)/areas/[areaSlug]/sites/[siteSlug]/_components/site-about-overview";
import { SiteHero } from "../../../(default)/areas/[areaSlug]/sites/[siteSlug]/_components/site-hero";
import { DxAccessSection } from "../../../(default)/areas/_components/dx-access-section";
import { DxFaqSection } from "../../../(default)/areas/_components/dx-faq-section";
import type { SiteDetailDrawerQuery } from "../../../../__generated__/gql/graphql";
import { useGtm } from "../../../../lib/google-tag-manager/gtm";
import { groupRoomTypesByArchitectureType } from "../../room-type-reservation/_utils/group-room-types-by-architecture-type";
import { isSingleSelection } from "../../room-type-reservation/_utils/is-single-selection";
import {
  isArchitectureTypeGroupUnavailableForRange,
  isRoomTypeUnavailable,
} from "../../room-type-reservation/_utils/room-type-availability-view";
import { keepRoomTypeForRange } from "../../room-type-reservation/_utils/select-visible-areas";
import { ArchitectureTypeGroup } from "../../room-type-reservation/browse/results/architecture-type-group";
import { SingleSelectionArchitecture } from "../../room-type-reservation/browse/results/single-selection-architecture";
import { usePendingStaySelectionContext } from "../_context/pending-stay-selection-context";

import { SiteDetailRoomsEmptyState } from "./site-detail-rooms-empty-state";
import { SiteDetailRoomsHeader } from "./site-detail-rooms-header";

type Site = NonNullable<SiteDetailDrawerQuery["siteBySlug"]>;

/**
 * 拠点詳細モーダルの本文。拠点ページ（`SitePage`）と異なり SEO 用 JSON-LD とエリア情報リンクは持たない。
 */
export function SiteDetailBody({
  site,
  siteSlug,
  openedFromCalendarCell,
}: {
  site: Site;
  siteSlug: string;
  /** 拠点別カレンダーのセルタップ経由で開いたか。rt_rsv_calendar_cell_clicked の送信条件に使う */
  openedFromCalendarCell: boolean;
}) {
  // 呼び出し元（site-detail-drawer.tsx）は必ず PendingStaySelectionProvider の子孫として
  // マウントするため throw 版を使う。確定済み検索条件（reserveCtx）ではなく、ドロワー内で
  // 選択中の一時日程を基準に連泊可否を判定する。
  const pendingStayCtx = usePendingStaySelectionContext();
  const gtm = useGtm();
  // ブラウズ一覧と同じ「1室で連泊をカバーできるか」判定（select-visible-areas.ts）を流用し、
  // 満室なのに予約可能に見える不整合を防ぐ。pendingStayCtx が null/null（真に日程未選択）のときは
  // 一覧側の dateRange: null と同じ扱いで判定自体をスキップする（today 1泊だけ空きが無い拠点が
  // 誤って満室表示になるのを防ぐ）。
  const roomTypes = site.roomTypesForReservation.map((roomType) => ({
    ...roomType,
    unavailableForRange:
      pendingStayCtx.firstNight !== null &&
      !keepRoomTypeForRange(
        roomType,
        pendingStayCtx.firstNight,
        pendingStayCtx.lastNight,
      ),
  }));
  const groups = groupRoomTypesByArchitectureType(roomTypes);

  // availabilities は $withDeferred の二段フェッチで後から届くため、全 roomType 分揃った
  // （満室判定が確定した）タイミングを都度判定し、effect 側では最初の1回だけ送る。
  const allAvailabilitiesLoaded = roomTypes.every(
    (rt) => rt.availabilities !== undefined,
  );
  const availableRoomTypeCount = roomTypes.filter(
    (rt) => !isRoomTypeUnavailable(rt),
  ).length;

  // モーダルは拠点ごとに再マウントされるため、useRef の送信済みフラグはその都度リセットされる。
  const cellClickedSentRef = useRef(false);
  useEffect(() => {
    // 拠点名/画像タップ（rt_rsv_site_detail_clicked）経由で開いた場合はセルタップ由来のイベントを送らない。
    // openedFromCalendarCell が true のときは selectedNight が必ず存在するため pendingStayCtx.firstNight
    // は null にならないが、TypeScript 上は絞り込みが必要。
    if (
      !openedFromCalendarCell ||
      cellClickedSentRef.current ||
      !allAvailabilitiesLoaded ||
      pendingStayCtx.firstNight === null
    )
      return;
    cellClickedSentRef.current = true;
    gtm.sendEvent("rt_rsv_calendar_cell_clicked", {
      site_slug: siteSlug,
      available_room_type_count: String(availableRoomTypeCount),
      // GTM は宿泊範囲をユーザー視点（CI〜CO）で送る方針のため、lastNight ではなく CO 日（+1日）を渡す。
      range_start: toPlainDateString(pendingStayCtx.firstNight),
      range_end: toPlainDateString(pendingStayCtx.lastNight.add({ days: 1 })),
    });
  }, [
    openedFromCalendarCell,
    allAvailabilitiesLoaded,
    availableRoomTypeCount,
    pendingStayCtx.firstNight,
    pendingStayCtx.lastNight,
    gtm,
    siteSlug,
  ]);

  return (
    <>
      <SiteHero site={site} showAreaLink={false} />
      {/* SiteAbout(拠点ページ本体)の section が持っていた px/py。1カラム表示なので lg:grid は不要 */}
      <div className="xs:py-[16px] px-[16px]">
        <SiteAboutOverview site={site} />
      </div>
      <div className="bg-dx-border-default h-px w-full" />
      <SiteDetailRoomsHeader
        firstNight={pendingStayCtx.firstNight}
        lastNight={pendingStayCtx.lastNight}
      />
      {/* EV 充電器などの絞り込みで roomTypesForReservation が 0 件になる拠点も空状態にする */}
      {roomTypes.length === 0 ||
      isArchitectureTypeGroupUnavailableForRange(roomTypes) ? (
        <SiteDetailRoomsEmptyState siteSlug={siteSlug} />
      ) : (
        <div className="flex flex-col">
          {groups.map((group, index) =>
            isSingleSelection(group.roomTypes) ? (
              <SingleSelectionArchitecture
                key={group.key}
                roomTypes={group.roomTypes}
                firstItem={index === 0}
                showBorder={false}
              />
            ) : (
              <ArchitectureTypeGroup
                key={group.key}
                roomTypes={group.roomTypes}
                firstItem={index === 0}
                showBorder={false}
              />
            ),
          )}
        </div>
      )}
      <div className="bg-dx-border-default h-px w-full" />
      <div className="xs:py-[16px] px-[16px]">
        <SiteAboutFacilities site={site} />
      </div>
      <DxAccessSection site={site} />
      <DxFaqSection site={site} />
    </>
  );
}
